import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, OnInit, Optional, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MaterialModule } from 'src/app/shared/modules/material.module';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ImportWizardStorage, ImportWizardStep, TabularColumns, typeOptions } from '../../../import-wizard.storage';
import { MatDialogRef } from '@angular/material/dialog';
import { ImportWizardComponent } from '../../../import-wizard.component';
import { ImportWizardGeoSpatialService } from '../import-wizard-geospatial.service';
import { FormsModule } from '@angular/forms';
import { ObservationCardComponent } from 'src/app/shared/card/observation-card/observation-card.component';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { ArborProcessingCardComponent } from 'src/app/shared/card/arbor-processing-card/arbor-processing-card.component';
import { SpecJson, ColumnsToRename, ColumnsAndType } from 'src/app/dtos/import-wizard/import-wizard.dto';
import { ToastrService } from 'ngx-toastr';
import { capitalize } from 'src/app/utils/string.utils';
import { ImportWizardGeoSpatialValidationHandler } from '@/app/handlers/import-wizard/import-wizard-geospatial-validation.handler';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface NewTypeInterface { label: string, value: string }

@Component({
    selector: 'app-import-wizard-geospatial-mapping_adjust',
    templateUrl: './import-wizard-geospatial-mapping_adjust.component.html',
    styleUrls: ['./import-wizard-geospatial-mapping_adjust.component.scss'],
    providers: [ImportWizardGeoSpatialValidationHandler],
    imports: [
        CommonModule,
        MaterialModule,
        ScrollingModule,
        FormsModule,
        ObservationCardComponent,
        AlertComponent,
        ArborProcessingCardComponent
    ],
    standalone: true
})
export class ImportWizardGeoSpatialMappingAdjustComponent implements OnInit, OnDestroy {

    // IMPORT WIZARD STATE
    _state = computed(() => this.importWizardStorage.state());
    _isMultiFeatured = computed(() => this.importWizardStorage.state().MultiFeatured);

    // DATA
    serviceColumns = computed(() => this.importWizardStorage.columns());
    columns_names = computed(() => this.serviceColumns().map(col => col.name));
    tabularRows = computed(() => this.importWizardStorage.rows());
    tabularTableDataSource = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        const end = start + this.pageSize();
        return this.tabularRows().slice(start, end);
    });
    _columns = signal<TabularColumns[]>([]);
    displayColumns = computed(() => this.importWizardStorage.displayColumns());

    // MAPPINGS
    columnNameChanges = new Map<string, string>();

    // PAGINATION
    pageSize = signal(10);
    currentPage = signal(1);

    // LOADING
    loadingProgress = signal<number>(0);
    validationLabel = signal<string>('Iniciando validação...');

    // FLAGS
    isFinishing = signal<boolean>(false);
    isAlertVisible = false;
    isLoading = signal<boolean>(this._state().step <= ImportWizardStep.MAPPING_ADJUSTS);

    buttonLabel = computed(() => {
        if (this.isMappingStep) {
            return this.isFinishing() ? 'Validando...' : 'Validar dados';
        }
        return 'Próximo';
    });

    _isColumnsInitialized = false;

    constructor(
        public importWizardStorage: ImportWizardStorage,
        private importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private toastrService: ToastrService,
        private sanitizer: DomSanitizer,
        private validationHandler: ImportWizardGeoSpatialValidationHandler,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>
    ) {
        this.validationHandler.progress$
            .pipe(takeUntilDestroyed())
            .subscribe(progress => {
                // Math.max garante que o fake progress nunca regrida
                this.loadingProgress.set(Math.max(this.loadingProgress(), progress));
                if (progress >= 100) this.isFinishing.set(false);
            });

        this.validationHandler.label$
            .pipe(takeUntilDestroyed())
            .subscribe(label => this.validationLabel.set(label));

        this.validationHandler.error$
            .pipe(takeUntilDestroyed())
            .subscribe(err => {
                this.isFinishing.set(false);
                this.toastrService.error(err, 'Erro na validação', { closeButton: true });
            });

        effect(() => {
            const cols = this.serviceColumns();

            if (cols.length > 0 && !this._isColumnsInitialized && this.isMappingStep) {
                this._columns.set(
                    cols.map(col => ({ ...col, name: this.normalizeText(col.name) }))
                );
                this.importWizardStorage.setDisplayColumns(this._columns());
                this._isColumnsInitialized = true;
            }
        });
    }

    ngOnInit(): void {
        this.validationHandler.init(this._state().id, this.currentPage(), this.pageSize());
        this.handleArborThinking();
    }

    private handleArborThinking(): void {
        const random = Math.floor(Math.random() * (3300 - 1500 + 1)) + 1500;
        setTimeout(() => this.isLoading.set(false), random);
    }

    onColumnNameChange(newName: string, column: TabularColumns, columnIndex: number): void {
        this._columns.update(cols =>
            cols.map(col => col === column ? { ...col, name: newName } : col)
        );
        this.importWizardStorage.setDisplayColumns(this._columns());
        this.importWizardStorage.setMappingHasChanges(true);

        const originalColumnName = this.serviceColumns()[columnIndex].name;
        this.columnNameChanges.set(originalColumnName, newName);
    }

    private buildSpecJson(): SpecJson {
        const columns_to_rename: ColumnsToRename[] = [];
        const columns_and_types: ColumnsAndType[] = [];

        this._columns().forEach((col, index) => {
            const originalName = this.importWizardStorage.columns()[index].name;
            const newName = this.columnNameChanges.get(originalName);
            if (newName !== originalName) {
                columns_to_rename.push({
                    column_name: originalName,
                    new_name: newName ?? col.name
                });
            }
        });

        return { columns_to_rename, columns_and_types };
    }

    trackByColumnIndex(index: number, column: TabularColumns): any {
        return index;
    }

    trackByColumnName(index: number, columnName: string): string {
        return columnName;
    }

    handleButtonClick(): void {
        // Return if import job isn't in mapping step (like next instead of validate button)
        if (!this.isMappingStep) {
            if (this._isMultiFeatured()) {
                this.importWizardStorage.redirectInViewStep(ImportWizardStep.FEATURES_LIST);
                return;
            }
            this.importWizardStorage.redirectInViewStep(ImportWizardStep.VALIDATION_CONFIRM);
            return;
        }

        const specJson = this.buildSpecJson();

        this.isFinishing.set(true);

        this.importWizardGeoSpatialService.postAddSpecificationToImportWizard(
            this._state().id,
            specJson
        ).subscribe({
            next: () => {
                this.validationHandler.start({
                    importId: this._state().id,
                    columns: this._columns(),
                    page: this.currentPage(),
                    pageSize: this.pageSize()
                });
            },
            error: (error) => {
                this.isFinishing.set(false);
                console.error('Erro ao enviar especificação', error);
                this.toastrService.error('Erro ao enviar especificação. Tente novamente.', '', { closeButton: true });
            }
        });
    }

    private normalizeText(value: string): string {
        return capitalize(value.replace(/[-_]+/g, ' '));
    }

    handleCancelClick(): void {
        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;
        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }

    get isMappingStep(): boolean {
        return this._state().step === ImportWizardStep.MAPPING_ADJUSTS;
    }

    ngOnDestroy(): void {
        this.importWizardStorage.setMappingHasChanges(false);
    }

    typeOptions = typeOptions;
}